import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-hot-toast";
import Layout from "../../components/Layout";
import { sideBarItems } from "../../utils";
import AdminService from "../../services/admin/AdminService";

const RATE_FIELDS = [
  "baseFare",
  "perKm",
  "perMinute",
  "platformFee",
  "minimumFare",
];

function FareConfigPage() {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState(null);
  const [initialConfig, setInitialConfig] = useState(null);
  const [vehicleMetadata, setVehicleMetadata] = useState({});
  const [validVehicleTypes, setValidVehicleTypes] = useState([]);
  const [meta, setMeta] = useState(null);

  const getError = (err) => err?.response?.data?.error || "Operation failed";

  const vehicleTypes = useMemo(() => {
    if (Array.isArray(validVehicleTypes) && validVehicleTypes.length) {
      return validVehicleTypes;
    }
    if (config?.baseFare) {
      return Object.keys(config.baseFare);
    }
    return [];
  }, [validVehicleTypes, config]);

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    setLoading(true);
    try {
      const res = await AdminService.getFareConfigV2();
      const payload = res?.data || {};
      const cfg = payload?.config || null;

      setConfig(cfg);
      setInitialConfig(cfg ? JSON.parse(JSON.stringify(cfg)) : null);
      setVehicleMetadata(payload?.vehicleMetadata || {});
      setValidVehicleTypes(payload?.validVehicleTypes || []);
      setMeta(payload?.meta || null);
    } catch (err) {
      toast.error(getError(err));
    } finally {
      setLoading(false);
    }
  };

  const onValueChange = (groupKey, vehicleType, value) => {
    if (!config) return;

    const parsed = value === "" ? "" : Number(value);
    setConfig((prev) => ({
      ...prev,
      [groupKey]: {
        ...prev[groupKey],
        [vehicleType]: parsed,
      },
    }));
  };

  const onSurgeChange = (value) => {
    const parsed = value === "" ? "" : Number(value);
    setConfig((prev) => ({ ...prev, surgeMultiplier: parsed }));
  };

  const onDriverFareAdjustmentChange = (field, value) => {
    setConfig((prev) => {
      const current = prev?.driverFareAdjustment || {
        enabled: false,
        type: "percentage",
        value: 0,
      };

      if (field === "enabled") {
        return {
          ...prev,
          driverFareAdjustment: {
            ...current,
            enabled: !!value,
          },
        };
      }

      if (field === "type") {
        return {
          ...prev,
          driverFareAdjustment: {
            ...current,
            type: value === "fixed" ? "fixed" : "percentage",
          },
        };
      }

      const parsed = value === "" ? "" : Number(value);
      return {
        ...prev,
        driverFareAdjustment: {
          ...current,
          value: parsed,
        },
      };
    });
  };

  const onLocationSelectionRadiusChange = (value) => {
    const parsed = value === "" ? "" : Number(value);
    setConfig((prev) => ({
      ...prev,
      locationSelectionRadiusKm: parsed,
    }));
  };

  const onDriverSearchRadiusChange = (value) => {
    const parsed = value === "" ? "" : Number(value);
    setConfig((prev) => ({
      ...prev,
      driverSearchRadiusKm: parsed,
    }));
  };

  const onTrialDurationDaysChange = (value) => {
    const parsed = value === "" ? "" : Number(value);
    setConfig((prev) => ({
      ...prev,
      trialDurationDays: parsed,
    }));
  };

  const validate = () => {
    if (!config) {
      toast.error("Fare config is missing");
      return false;
    }

    for (const field of RATE_FIELDS) {
      for (const vehicleType of vehicleTypes) {
        const value = config?.[field]?.[vehicleType];
        if (typeof value !== "number" || Number.isNaN(value) || value < 0) {
          toast.error(`Invalid ${field} for ${vehicleType}`);
          return false;
        }
      }
    }

    if (
      typeof config?.surgeMultiplier !== "number" ||
      Number.isNaN(config.surgeMultiplier) ||
      config.surgeMultiplier < 1
    ) {
      toast.error("surgeMultiplier must be a number >= 1");
      return false;
    }

    const adjustment = config?.driverFareAdjustment || {};
    if (typeof adjustment?.enabled !== "boolean") {
      toast.error("driverFareAdjustment.enabled must be boolean");
      return false;
    }
    if (!["percentage", "fixed"].includes(adjustment?.type)) {
      toast.error("driverFareAdjustment.type must be percentage or fixed");
      return false;
    }
    if (
      typeof adjustment?.value !== "number" ||
      Number.isNaN(adjustment.value) ||
      adjustment.value < 0
    ) {
      toast.error("driverFareAdjustment.value must be >= 0");
      return false;
    }
    if (adjustment.type === "percentage" && adjustment.value > 100) {
      toast.error(
        "driverFareAdjustment.value cannot exceed 100 for percentage",
      );
      return false;
    }

    if (
      typeof config?.locationSelectionRadiusKm !== "number" ||
      Number.isNaN(config.locationSelectionRadiusKm) ||
      config.locationSelectionRadiusKm <= 0
    ) {
      toast.error("locationSelectionRadiusKm must be a number > 0");
      return false;
    }

    if (
      typeof config?.driverSearchRadiusKm !== "number" ||
      Number.isNaN(config.driverSearchRadiusKm) ||
      config.driverSearchRadiusKm <= 0
    ) {
      toast.error("driverSearchRadiusKm must be a number > 0");
      return false;
    }

    if (
      typeof config?.trialDurationDays !== "number" ||
      Number.isNaN(config.trialDurationDays) ||
      !Number.isInteger(config.trialDurationDays) ||
      config.trialDurationDays <= 0
    ) {
      toast.error("trialDurationDays must be a positive integer");
      return false;
    }

    return true;
  };

  const buildPatch = () => {
    if (!config || !initialConfig) {
      return {};
    }

    const patch = {};

    RATE_FIELDS.forEach((field) => {
      const currentGroup = config[field] || {};
      const initialGroup = initialConfig[field] || {};
      const groupPatch = {};

      Object.keys(currentGroup).forEach((vehicleType) => {
        if (currentGroup[vehicleType] !== initialGroup[vehicleType]) {
          groupPatch[vehicleType] = currentGroup[vehicleType];
        }
      });

      if (Object.keys(groupPatch).length) {
        patch[field] = groupPatch;
      }
    });

    if (config.surgeMultiplier !== initialConfig.surgeMultiplier) {
      patch.surgeMultiplier = config.surgeMultiplier;
    }

    if (
      config.locationSelectionRadiusKm !==
      initialConfig.locationSelectionRadiusKm
    ) {
      patch.locationSelectionRadiusKm = config.locationSelectionRadiusKm;
    }

    if (config.driverSearchRadiusKm !== initialConfig.driverSearchRadiusKm) {
      patch.driverSearchRadiusKm = config.driverSearchRadiusKm;
    }

    if (config.trialDurationDays !== initialConfig.trialDurationDays) {
      patch.trialDurationDays = config.trialDurationDays;
    }

    const currentAdjustment = config?.driverFareAdjustment || {};
    const initialAdjustment = initialConfig?.driverFareAdjustment || {};
    if (
      currentAdjustment.enabled !== initialAdjustment.enabled ||
      currentAdjustment.type !== initialAdjustment.type ||
      currentAdjustment.value !== initialAdjustment.value
    ) {
      patch.driverFareAdjustment = {
        enabled: !!currentAdjustment.enabled,
        type: currentAdjustment.type === "fixed" ? "fixed" : "percentage",
        value: Number(currentAdjustment.value || 0),
      };
    }

    return patch;
  };

  const save = async () => {
    if (!validate()) {
      return;
    }

    const patch = buildPatch();
    if (!Object.keys(patch).length) {
      toast.error("No changes detected");
      return;
    }

    setSaving(true);
    try {
      const res = await AdminService.updateFareConfigV2(patch);
      const payload = res?.data || {};
      const cfg = payload?.config || null;

      setConfig(cfg);
      setInitialConfig(cfg ? JSON.parse(JSON.stringify(cfg)) : null);
      setMeta(payload?.meta || null);

      toast.success(payload?.message || "Fare config updated successfully");
    } catch (err) {
      toast.error(getError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Layout title={sideBarItems.fareConfig.key}>
      <div className="w-full py-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-6">Fare Config</h1>

        <div className="bg-white shadow-md rounded-lg p-6 mb-6">
          <div className="flex items-end justify-between gap-4 mb-4">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                Ride Fare Settings
              </h2>
              <p className="text-sm text-gray-500">
                Keys are fixed. Only values are editable.
              </p>
            </div>
            <button
              onClick={save}
              disabled={loading || saving || !config}
              className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50"
            >
              {saving ? "Saving..." : "Save Changes"}
            </button>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full border border-gray-200">
              <thead>
                <tr className="bg-gray-50 text-left text-sm text-gray-600">
                  <th className="px-4 py-2 border">Vehicle Type</th>
                  <th className="px-4 py-2 border">Base Fare</th>
                  <th className="px-4 py-2 border">Per KM</th>
                  <th className="px-4 py-2 border">Per Minute</th>
                  <th className="px-4 py-2 border">Platform Fee</th>
                  <th className="px-4 py-2 border">Minimum Fare</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr>
                    <td
                      colSpan="6"
                      className="px-4 py-10 text-center border text-gray-500"
                    >
                      Loading...
                    </td>
                  </tr>
                )}
                {!loading &&
                  vehicleTypes.map((vehicleType) => (
                    <tr key={vehicleType} className="text-sm">
                      <td className="px-4 py-2 border">
                        <div className="font-medium">
                          {vehicleMetadata?.[vehicleType]?.name || vehicleType}
                        </div>
                      </td>
                      {RATE_FIELDS.map((field) => (
                        <td
                          key={`${vehicleType}-${field}`}
                          className="px-4 py-2 border"
                        >
                          <input
                            type="number"
                            min="0"
                            className="w-28 px-2 py-1 border rounded"
                            value={config?.[field]?.[vehicleType] ?? ""}
                            onChange={(e) =>
                              onValueChange(field, vehicleType, e.target.value)
                            }
                          />
                        </td>
                      ))}
                    </tr>
                  ))}
                {!loading && !vehicleTypes.length && (
                  <tr>
                    <td
                      colSpan="6"
                      className="px-4 py-10 text-center border text-gray-500"
                    >
                      No fare config found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="bg-white shadow-md rounded-lg p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Location Selection Radius
          </h2>
          <p className="text-sm text-gray-500 mb-4">
            After the first pickup, drop, or stop is selected, all later
            locations must stay within this radius.
          </p>
          <div className="flex items-center gap-3">
            <label className="text-sm font-medium text-gray-700">
              Radius (km)
            </label>
            <input
              type="number"
              min="0.1"
              step="0.1"
              className="w-32 px-2 py-1 border rounded"
              value={config?.locationSelectionRadiusKm ?? ""}
              onChange={(e) => onLocationSelectionRadiusChange(e.target.value)}
            />
          </div>
        </div>

        <div className="bg-white shadow-md rounded-lg p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Driver Request Radius
          </h2>
          <p className="text-sm text-gray-500 mb-4">
            Ride requests are sent to online, available drivers within this
            pickup radius.
          </p>
          <div className="flex items-center gap-3">
            <label className="text-sm font-medium text-gray-700">
              Radius (km)
            </label>
            <input
              type="number"
              min="0.1"
              step="0.1"
              className="w-32 px-2 py-1 border rounded"
              value={config?.driverSearchRadiusKm ?? ""}
              onChange={(e) => onDriverSearchRadiusChange(e.target.value)}
            />
          </div>
        </div>

        <div className="bg-white shadow-md rounded-lg p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Free Trial Duration
          </h2>
          <p className="text-sm text-gray-500 mb-4">
            Set how many days a new driver gets for the one-time free trial.
          </p>
          <div className="flex items-center gap-3">
            <label className="text-sm font-medium text-gray-700">
              Trial Days
            </label>
            <input
              type="number"
              min="1"
              step="1"
              className="w-32 px-2 py-1 border rounded"
              value={config?.trialDurationDays ?? ""}
              onChange={(e) => onTrialDurationDaysChange(e.target.value)}
            />
          </div>
        </div>

        <div className="bg-white shadow-md rounded-lg p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Surge Pricing
          </h2>
          <div className="flex items-center gap-3">
            <label className="text-sm font-medium text-gray-700">
              Surge Multiplier
            </label>
            <input
              type="number"
              min="1"
              step="0.1"
              className="w-32 px-2 py-1 border rounded"
              value={config?.surgeMultiplier ?? ""}
              onChange={(e) => onSurgeChange(e.target.value)}
            />
          </div>
        </div>

        <div className="bg-white shadow-md rounded-lg p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Driver Fare Adjustment
          </h2>
          <p className="text-sm text-gray-500 mb-4">
            Reduce driver-visible fare before bidding and bid acceptance
            calculations.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
              <input
                type="checkbox"
                checked={!!config?.driverFareAdjustment?.enabled}
                onChange={(e) =>
                  onDriverFareAdjustmentChange("enabled", e.target.checked)
                }
              />
              Enable adjustment
            </label>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Type
              </label>
              <select
                className="w-full px-2 py-2 border rounded"
                value={config?.driverFareAdjustment?.type || "percentage"}
                onChange={(e) =>
                  onDriverFareAdjustmentChange("type", e.target.value)
                }
              >
                <option value="percentage">Percentage</option>
                <option value="fixed">Fixed (BDT)</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Value{" "}
                {config?.driverFareAdjustment?.type === "fixed"
                  ? "(BDT)"
                  : "(%)"}
              </label>
              <input
                type="number"
                min="0"
                max={
                  config?.driverFareAdjustment?.type === "percentage"
                    ? "100"
                    : undefined
                }
                step="0.1"
                className="w-full px-2 py-2 border rounded"
                value={config?.driverFareAdjustment?.value ?? 0}
                onChange={(e) =>
                  onDriverFareAdjustmentChange("value", e.target.value)
                }
              />
            </div>
          </div>
        </div>

        <div className="bg-white shadow-md rounded-lg p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-3">Meta</h2>
          <p className="text-sm text-gray-700">
            <span className="font-medium">Version:</span>{" "}
            {meta?.version ?? "N/A"}
          </p>
          <p className="text-sm text-gray-700">
            <span className="font-medium">Updated At:</span>{" "}
            {meta?.updatedAt
              ? new Date(meta.updatedAt).toLocaleString()
              : "N/A"}
          </p>
          <p className="text-sm text-gray-700">
            <span className="font-medium">Updated By:</span>{" "}
            {meta?.updatedBy?.name || meta?.updatedBy?.mobile || "N/A"}
          </p>
        </div>
      </div>
    </Layout>
  );
}

export default FareConfigPage;
